import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import { Marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import { PRODUCTION_ENVIRONMENTS } from '../../../common/constants';
import type { WidgetEnvironment } from '../../../common/sgwt-widgets-utils';

// Make the copy-to-clipboard buttons attached to code blocks work.
export function enableCopyButtons() {
  const buttons = document.querySelectorAll<HTMLButtonElement>('button.hljs-copy-button');
  // biome-ignore lint/complexity/noForEach: it's the .forEach of NodeList object.
  buttons.forEach((button: HTMLButtonElement) => {
    button.addEventListener('click', () => {
      const code = button.dataset.code;
      if (code) {
        const decoded = atob(code);
        try {
          navigator.clipboard.writeText(decoded);
        } catch (error) {
          console.error('Could not copy to clipboard', error);
        }
      }
    });
  });
}

// Use highlight.js to highlight code blocks in the chat messages.
const marked = new Marked(
  markedHighlight({
    emptyLangClass: 'hljs',
    langPrefix: 'hljs language-',
    highlight(code, lang, _info) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext';
      const formatted = hljs.highlight(code, { language });
      return formatted.value;
    },
  }),
);

// Transform some rendering options of marked.
marked.use({
  renderer: {
    // Set default class for all links and open in new tab.
    link({ href, title, text }: { href: string; title?: string | null; text: string }): string {
      const sanitizedHref = DOMPurify.sanitize(href);
      const sanitizedTitle = title ? DOMPurify.sanitize(title) : '';
      const sanitizedText = DOMPurify.sanitize(text);
      // ? `target="_blank"` are removed by DOMPurify, so we add them back in `markdownToHtml()`
      return `<a class="text-link" href="${sanitizedHref}" title="${sanitizedTitle}" target="_blank" rel="noopener noreferrer">${sanitizedText}</a>`;
    },

    heading({ text, depth }: { text: string; depth: number }): string {
      const sanitizedText = DOMPurify.sanitize(text);
      return `<h${depth} class="fs-16">${sanitizedText}</h${depth}>`;
    },

    // Add the copy-to-clipboard button to code blocks.
    code({ lang, raw, text }: { text: string; lang?: string; raw: string }) {
      const code = raw
        .replace(/^\s*```\w+\s/, '') // remove the first line with the language
        .replace(/```\s*$/, ''); // remove the last line with ```
      const encoded = btoa(code);

      return `
        <div class="bg-lvl2 d-flex my-3">
          <pre class="flex-grow-1 mb-0"><code class="hljs hljs-${lang} bg-lvl2 scrollbar-sm" style="font-size: 0.9rem !important;">${text}</code></pre>
          <div>
            <button class="btn btn-flat-secondary btn-lg btn-icon hljs-copy-button" title="Copy to clipboard" data-code="${encoded}">
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" id="content_copy" data-testid="content_copy" class="icon " fill="currentColor">
                <path d="M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z"></path>
                <path d="M0 0h24v24H0z" fill="none"></path>
              </svg>
            </button>
          </div>
        </div>`;
    },
  },
});

export function markdownToHtml(md: string): string {
  const html = marked.parse(md) as string;
  const sanitizedHtml = DOMPurify.sanitize(html);
  // DOMPurify removes the `target="_blank"` (https://github.com/cure53/DOMPurify/issues/1123#issuecomment-3084008308), so we add them back here.
  return sanitizedHtml.replaceAll('<a ', '<a target="_blank" ');
}

// Scroll down to the latest message
export function scrollToLatestMessage() {
  const container = document.querySelector<HTMLDivElement>('sgwt-chatbot .sgwt-chatbot-body');
  if (container) {
    setTimeout(() => {
      const lastMessage = document.querySelector<HTMLDivElement>('.sgwt-chatbot-messages .sgwt-chatbot-message:last-child');
      if (lastMessage) {
        container.scrollTo({
          top: lastMessage.offsetTop,
          behavior: 'smooth',
        });
      }
    }, 100);
  }
}

export const getEnvironment = (): WidgetEnvironment => {
  const { environment } = window.SGWTWidgetConfiguration;
  return !environment || PRODUCTION_ENVIRONMENTS.includes(environment.toLowerCase()) ? 'production' : 'homologation';
};
